import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react';

const base = (invalid: boolean, extra = '') =>
  `w-full rounded-lg border px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 ${
    invalid ? 'border-red-400 bg-red-50/40 focus:ring-red-300' : 'border-gray-300 focus:ring-agrida-500'
  } ${extra}`;

function ClearButton({ onClear, label, offset = false }: { onClear: () => void; label?: string; offset?: boolean }) {
  return (
    <button
      type="button"
      tabIndex={-1}
      onClick={onClear}
      aria-label={`Xóa ${label ?? 'nội dung'}`}
      title="Xóa để nhập lại"
      className={`absolute ${offset ? 'right-9' : 'right-1.5'} top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600`}
    >
      <i className="ri-close-line text-base" />
    </button>
  );
}

export function FieldError({ message }: { message?: string }) {
  return message ? <p className="mt-1 text-[11px] text-red-600">{message}</p> : null;
}

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string;
  onValueChange: (value: string) => void;
  /** Shown in red under the input; the border turns red too. */
  error?: string;
  /** Overrides the default input classes (pr-9 is kept for the ✕). */
  inputClassName?: string;
};

/**
 * An input with a ✕ to clear it and re-type — used on every form AI fills, so
 * a wrong reading can be wiped in one tap — and an inline red error.
 */
export function ClearableInput({ value, onValueChange, error, inputClassName, className, disabled, ...rest }: InputProps) {
  // A date input keeps the browser's own calendar button at the right edge.
  const isDate = rest.type === 'date';
  return (
    <div className={className}>
      <div className="relative">
        <input
          {...rest}
          disabled={disabled}
          value={value}
          aria-invalid={Boolean(error)}
          onChange={(e) => onValueChange(e.target.value)}
          className={`${inputClassName ? `${inputClassName} pr-9 ${error ? 'border-red-400 bg-red-50/40' : ''}` : base(Boolean(error))} ${isDate ? 'pr-16' : ''}`}
        />
        {value && !disabled && (
          <ClearButton offset={isDate} onClear={() => onValueChange('')} label={typeof rest['aria-label'] === 'string' ? rest['aria-label'] : rest.placeholder} />
        )}
      </div>
      <FieldError message={error} />
    </div>
  );
}

type TextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> & {
  value: string;
  onValueChange: (value: string) => void;
  error?: string;
  inputClassName?: string;
};

export function ClearableTextarea({ value, onValueChange, error, inputClassName, className, disabled, ...rest }: TextareaProps) {
  return (
    <div className={className}>
      <div className="relative">
        <textarea
          {...rest}
          disabled={disabled}
          value={value}
          aria-invalid={Boolean(error)}
          onChange={(e) => onValueChange(e.target.value)}
          className={inputClassName ? `${inputClassName} pr-9 ${error ? 'border-red-400 bg-red-50/40' : ''}` : base(Boolean(error))}
        />
        {value && !disabled && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => onValueChange('')}
            aria-label="Xóa nội dung"
            title="Xóa để nhập lại"
            className="absolute right-1.5 top-2 flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <i className="ri-close-line text-base" />
          </button>
        )}
      </div>
      <FieldError message={error} />
    </div>
  );
}
